// Section 19 and 15: Birth complication. 10% for each birth this season. The mother and the
// newborn face the Section 15 childbirth risks again: 4% maternal and 8% infant death, or with a
// healer 2% (1% with cloth) and 4%. Choices: assign a healer now (they are pulled from someone
// else: one fewer patient treated this season) or do nothing.
// Constants: config.events['birth-complication'], config.lifecycle (maternal/infant death).
import type { Config } from '../../config';
import {
  choiceOutcome,
  choiceRng,
  effectDelta,
  eventEntry,
  eventPrompt,
  eventRng,
} from '../../core/content-helpers';
import { powInt } from '../../core/math';
import { mod } from '../../core/modifiers';
import { isWorking, skillOf } from '../../core/queries';
import { defineEvent, type EventChoiceDef } from '../../core/registry';
import type { Delta, EventPrompt, Modifier, Person, PersonId, ReadContext } from '../../core/types';

interface Birth {
  mother: Person;
  child: Person;
}

function births(ctx: ReadContext): Birth[] {
  const out: Birth[] = [];
  for (const e of ctx.state.currentReport?.entries ?? []) {
    if (e.kind !== 'birth') continue;
    const mother = ctx.state.people[e.motherId];
    const child = ctx.state.people[e.personId];
    if (mother !== undefined && child !== undefined) out.push({ mother, child });
  }
  return out;
}

function healer(ctx: ReadContext): Person | null {
  const healers = (Object.keys(ctx.state.people).sort() as PersonId[])
    .map((id) => ctx.state.people[id])
    .filter((p): p is Person => p !== undefined && p.job === 'healer' && isWorking(p))
    .sort((a, b) => skillOf(b, 'healer') - skillOf(a, 'healer') || (a.id < b.id ? -1 : 1));
  return healers[0] ?? null;
}

function risks(ctx: ReadContext, prompt: EventPrompt, attended: boolean, cloth: boolean): Delta[] {
  const l = ctx.cfg.lifecycle;
  const maternal = attended
    ? cloth
      ? l.maternalDeathHealerCloth
      : l.maternalDeathHealer
    : l.maternalDeath;
  const infant = attended ? l.infantDeathHealer : l.infantDeath;
  const rng = choiceRng(prompt, 'risk');
  const [motherId, childId] = prompt.subjectIds;
  const out: Delta[] = [];
  const m = rng.float();
  const i = rng.float();
  if (motherId !== undefined && ctx.state.people[motherId] !== undefined && m < maternal)
    out.push({
      kind: 'kill',
      personId: motherId,
      cause: 'childbirth',
      source: 'event:birth-complication',
    });
  if (childId !== undefined && ctx.state.people[childId] !== undefined && i < infant)
    out.push({
      kind: 'kill',
      personId: childId,
      cause: 'infant',
      source: 'event:birth-complication',
    });
  return out;
}

export function birthComplication(config: Config) {
  const c = config.events['birth-complication'];
  const choices: EventChoiceDef[] = [
    {
      id: 'assign-healer',
      label: 'Assign a healer now',
      preview: (ctx) => {
        const h = healer(ctx);
        return [
          { key: h === null ? 'birth-no-healer' : 'birth-healer', params: { name: h?.name ?? '' } },
        ];
      },
      apply: (ctx, prompt) => {
        const h = healer(ctx);
        if (h === null)
          return [
            ...risks(ctx, prompt, false, false),
            choiceOutcome(prompt, 'assign-healer', { name: '' }),
          ];
        const per = ctx.cfg.lifecycle.clothPerBirth;
        const cloth = ctx.state.stocks.cloth.amount >= per;
        const out: Delta[] = [
          effectDelta('birth-complication', 'healer', ctx.season.turn, 1, { healerSlots: -1 }, [
            h.id,
          ]),
        ];
        if (cloth)
          out.push({ kind: 'stock', resource: 'cloth', amount: -per, flow: 'consumption' });
        out.push(...risks(ctx, prompt, true, cloth));
        out.push(choiceOutcome(prompt, 'assign-healer', { name: h.name }));
        return out;
      },
    },
    {
      id: 'do-nothing',
      label: 'Do nothing',
      preview: () => [
        {
          key: 'birth-alone',
          params: {
            mother: Math.round(config.lifecycle.maternalDeath * 100),
            infant: Math.round(config.lifecycle.infantDeath * 100),
          },
        },
      ],
      apply: (ctx, prompt) => [
        ...risks(ctx, prompt, false, false),
        choiceOutcome(prompt, 'do-nothing'),
      ],
    },
  ];
  const plan = (ctx: ReadContext): Birth | null => {
    const all = births(ctx);
    return all.length === 0 ? null : eventRng(ctx, 'birth-complication', 'birth').pick(all);
  };
  return defineEvent({
    id: 'birth-complication',
    label: 'Birth complication',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['spring', 'summer', 'autumn', 'winter'],
    foreshadow: null,
    ledgerTag: null,
    weight: (ctx) => {
      const n = births(ctx).length;
      return n === 0 ? 0 : 1 - powInt(1 - c.baseChance, n);
    },
    text: (ctx) => {
      const b = plan(ctx);
      return eventEntry('birth-complication', {
        mother: b?.mother.name ?? '',
        child: b?.child.name ?? '',
      });
    },
    apply: (ctx) => {
      const b = plan(ctx);
      if (b === null) return [];
      return [
        eventPrompt(ctx, 'birth-complication', choices, {
          params: { mother: b.mother.name, child: b.child.name },
          subjectIds: [b.mother.id, b.child.id],
          defaultChoiceId: 'assign-healer',
        }),
      ];
    },
    choices,
    modifierTargets: ['output'],
    modifiers(_ctx, target, effect): Modifier[] {
      const slots = effect.params.healerSlots;
      if (target.kind !== 'output' || target.jobId !== 'healer' || slots === undefined) return [];
      return effect.subjectIds?.includes(target.personId) === true
        ? [mod.add('event:birth-complication', slots, 'attending a birth')]
        : [];
    },
  });
}
